import React, { useEffect } from 'react';
import QuestionPanel from "../../structure/QuestionPanel";
import DateInput from "../../input/DateInput";
import NumericInput from "../../input/NumericInput";
import { EmployerFormValues } from "../../common/EmployerFormValues";
import { WorkSchedule } from "../../enums/WorkSchedule";
import Checkbox from "../../input/Checkbox";
import { DisabilityEmployerFormValues } from "../../disability/DisabilityEmployerFormValues";
import RadioGroup from "../../structure/RadioGroup";
import DecimalInput from "../../input/DecimalInput";
import { LifeEmployerFormValues } from "../../life/LifeEmployerFormValues";
import SelectDropdown from "../../input/SelectDropdown";
import { Relationship } from "../../enums/Relationship";
import { WorkScheduleFrequency } from "../../enums/WorkScheduleFrequency";
import { DayOfWeek } from "../../enums/DayOfWeek";
import { useFormikContext } from "formik";
import { PfmlEmployerFormValues } from "../../pfml/PfmlEmployerFormValues";
import ShowHideDiv from "../../structure/ShowHideDiv";
import { EmployerReturnToWork } from "../../enums/EmployerReturnToWork";
import { usePfml1250MinimumRequiredHours } from "../../../hooks/usePfml1250MinimumRequiredHours";
const Employment = () => {
    const { initialValues, values, setFieldValue } = useFormikContext<EmployerFormValues>();
    const isCoveredDependent = Relationship.COVERED_DEPENDENT === values.relationship;
    const notActivelyAtWork = !isCoveredDependent || ["No", undefined].includes((values as any as LifeEmployerFormValues)?.activelyAtWork);
    const disabilityEmployerFormValues = values as DisabilityEmployerFormValues;
    const pfmlEmployerFormValues = values as PfmlEmployerFormValues;
    const workSchedule = pfmlEmployerFormValues.workSchedule;
    const rotatingWorkSchedule = WorkSchedule.ROTATING === workSchedule;
    const alternativeWorkSchedule = WorkSchedule.ALTERNATIVE === workSchedule;
    const weeklyWorkSchedule = pfmlEmployerFormValues.weeklyWorkSchedule;
    const { data: pfml1250MinimumRequiredHoursSupported } = usePfml1250MinimumRequiredHours(values.workState);
    const stateRequiresMinimumHoursWorked = values.pfml && pfml1250MinimumRequiredHoursSupported?.validatePfml1250MinimumRequiredHours === true;

    useEffect(() => {
        if([WorkSchedule.REGULAR, WorkSchedule.ROTATING].includes(workSchedule)) {
            const weeklyHours = weeklyWorkSchedule?.map((week) => {
                return (safeParseFloat(week.sunday) + safeParseFloat(week.monday) + safeParseFloat(week.tuesday) + safeParseFloat(week.wednesday) +
                    safeParseFloat(week.thursday) + safeParseFloat(week.friday) + safeParseFloat(week.saturday));
            });
            const hoursWorkedPerWeek = weeklyHours?.reduce((hoursWorkedEachWeek, hoursWorkedThisWeek) => {
                return hoursWorkedEachWeek + hoursWorkedThisWeek
            }, 0) / weeklyWorkSchedule?.length;
            setFieldValue('hoursWorkedPerWeek', hoursWorkedPerWeek.toString())
        }
    }, [workSchedule, weeklyWorkSchedule, setFieldValue])

    function safeParseFloat(value: string) {
        return parseFloat(value) || 0
    }

    return (
        <QuestionPanel headerText="When does your employee work?"
                       detailsText="Your employee's work schedule will determine their benefit payment, and their employment dates determine the eligibility and length of their claim."
                       panelContent={
                           <>
                               <div>
                                   <RadioGroup id="work-schedule" label="My employee works:" required={true} radioProps={[
                                       { id: "forty-hour", text: WorkSchedule.FORTY_HOUR, changeCallback: defaultFortyHourWorkWeek },
                                       { id: "regular", text: WorkSchedule.REGULAR, changeCallback: defaultBlankWorkWeek },
                                       { id: "rotating", text: WorkSchedule.ROTATING, changeCallback: defaultTwoBlankWorkWeeks },
                                       { id: "alternative", text: WorkSchedule.ALTERNATIVE, changeCallback: defaultEmptyWorkWeek }
                                   ]}/>
                                   {[WorkSchedule.REGULAR, WorkSchedule.ROTATING].includes(workSchedule as WorkSchedule) && <>
                                       <label>Please enter the number of hours the employee works each day{rotatingWorkSchedule ? ' for each week (odd weeks on first row, even weeks on second row)' : ''}.</label>
                                       <table className="pds-u-margin-bottom-0">
                                           <tbody>
                                               {buildDayInputs()}
                                               {rotatingWorkSchedule && buildDayInputs(1)}
                                           </tbody>
                                       </table>
                                   </>}
                                   {alternativeWorkSchedule && <SelectDropdown id="alternative-schedule-frequency"
                                                                               label="Is your employee provided their work schedule:"
                                                                               required={true}
                                                                               options={Object.keys(WorkScheduleFrequency).map(frequency => ({
                                                                                   id: frequency.replace(/_/g, '-').toLowerCase(),
                                                                                   text: (WorkScheduleFrequency as any)[frequency]
                                                                               }))}/>}
                                   <ShowHideDiv show={(initialValues as PfmlEmployerFormValues).workSchedule === undefined || alternativeWorkSchedule}>
                                       <DecimalInput id="hours-worked-per-week" label={`${alternativeWorkSchedule ? "Average hours" : "Hours"} worked per week`} required={true} maxLength={5}/>
                                   </ShowHideDiv>
                                   {alternativeWorkSchedule && <SelectDropdown id="average-days-worked-per-week"
                                                                               label="Which days does your employee work the average hours above per week?"
                                                                               required={true}
                                                                               multiple={true}
                                                                               options={Object.keys(DayOfWeek).map(day => ({
                                                                                   id: day.replace(/_/g, '-').toLowerCase(),
                                                                                   text: (DayOfWeek as any)[day]
                                                                               }))}/>}
                                   <DateInput id="date-of-hire" label="Date of hire" required={true}/>
                                   {isCoveredDependent &&
                                       <RadioGroup id="actively-at-work" label="Is your employee currently working?" required={true}
                                                   radioProps={[
                                                       { id: "yes", text: "Yes" },
                                                       { id: "no", text: "No" }
                                                   ]}/>}
                                   {notActivelyAtWork &&
                                       <>
                                           <SelectDropdown id="reason-ceased-active-work" label="Reason employee ceased active work"
                                                           required={true} options={[
                                               { id: "death", text: "Death" },
                                               { id: "sickness", text: "Sickness" },
                                               { id: "accident", text: "Accident" },
                                               { id: "pto/vacation", text: "PTO/Vacation" },
                                               { id: "terminated", text: "Terminated" },
                                               { id: "layoff", text: "Layoff" },
                                               { id: "retired", text: "Retired" },
                                               { id: "absence", text: "Leave of absence" },
                                               { id: "fmla", text: "FMLA" },
                                               { id: "resignation", text: "Resignation" },
                                               { id: "sabbatical", text: "Sabbatical" },
                                               { id: "other", text: "Other" }]}/>
                                           <DateInput id="date-last-worked" label="Date last worked" required={true} inputProps={{ helpText: 'Enter anticipated date if in the future' }}/>
                                       </>}
                                   <Checkbox id="normal-schedule-on-date-last-worked" label="Employee worked a normal schedule on date last worked"/>
                                   {!disabilityEmployerFormValues.normalScheduleOnDateLastWorked
                                       && <NumericInput id="hours-worked-on-date-last-worked" label="Hours worked on date last worked" maxLength={2} required={true}/>}
                                   <DateInput id="date-returned-to-work" label="Date returning to work" required={false} inputProps={{ helpText: 'Enter anticipated date if in the future' }}/>
                                   {disabilityEmployerFormValues.dateReturnedToWork
                                       && <RadioGroup id="return-to-work" label="Return to work schedule"
                                                      required={true}
                                                      radioProps={Object.keys(EmployerReturnToWork).map(employerReturnToWork => ({
                                                          id: employerReturnToWork.replace(/_/g, '-').toLowerCase(),
                                                          text: (EmployerReturnToWork as any)[employerReturnToWork]
                                                      }))}/>}
                                   {stateRequiresMinimumHoursWorked &&
                                       <RadioGroup id="minimum-required-hours-worked" label="Has this employee satisfied the 1250 hour work requirement in the last 12 months?" required={true}
                                                   radioProps={[
                                                       { id: "yes", text: "Yes" },
                                                       { id: "no", text: "No" }
                                                   ]}/>
                                   }
                               </div>
                           </>}
        >
        </QuestionPanel>
    )

    function defaultFortyHourWorkWeek() {
        setFieldValue('hoursWorkedPerWeek', '40')
        setFieldValue('weeklyWorkSchedule', [{
            sunday: '0',
            monday: '8',
            tuesday: '8',
            wednesday: '8',
            thursday: '8',
            friday: '8',
            saturday: '0',
        }]);
    }

    function defaultTwoBlankWorkWeeks() {
        setFieldValue('weeklyWorkSchedule', [{
            sunday: '',
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
        }, {
            sunday: '',
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
        }]);
    }

    function defaultBlankWorkWeek() {
        setFieldValue('weeklyWorkSchedule', [{
            sunday: '',
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
        }]);
    }

    function defaultEmptyWorkWeek() {
        setFieldValue('weeklyWorkSchedule', [])
    }
};

function buildDayInputs(index: number = 0) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const labels = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat']
    let dayInputs = [];
    for (let i = 0; i < labels.length; ++i) {
        const day = days[i]
        const abbreviation = labels[i]
        dayInputs.push(
            <td>
                <DecimalInput id={`weekly-work-schedule-${day}-${index}`} name={`weeklyWorkSchedule[${index}].${day}`}
                              required={false} maxLength={5} label={abbreviation}/>
            </td>)
    }
    return <>
        <tr>
            {dayInputs}
        </tr>
    </>;
}

export default Employment;
